import { useEffect, useState } from 'react';
import { cashEntriesApi, type CashEntryType, type CashMethod } from '../lib/api/cash-entries';
import { employeesApi, type EmployeePickerItem } from '../lib/api/employees';
import { SearchableDropdown } from './SearchableDropdown';

interface Props {
  open: boolean;
  preset: { entryType: CashEntryType; method: CashMethod } | null;
  onClose: () => void;
  onSaved: () => void;
}

const OFFSET_SUGGEST: Record<CashEntryType, Array<{ code: string; label: string }>> = {
  receipt: [
    { code: '131', label: '131 · Phải thu KH' },
    { code: '141', label: '141 · Tạm ứng NV' },
    { code: '338', label: '338 · Phải trả khác' },
    { code: '511', label: '511 · Doanh thu bán hàng' },
    { code: '5113', label: '5113 · DT dịch vụ' },
    { code: '711', label: '711 · Thu nhập khác' },
  ],
  payment: [
    { code: '331', label: '331 · Phải trả NCC' },
    { code: '3341', label: '3341 · Phải trả NLĐ' },
    { code: '6421', label: '6421 · CP nhân viên QLDN' },
    { code: '6427', label: '6427 · CP dịch vụ mua ngoài (QLDN)' },
    { code: '6428', label: '6428 · CP bằng tiền khác (QLDN)' },
    { code: '141', label: '141 · Tạm ứng NV' },
    { code: '811', label: '811 · Chi phí khác' },
  ],
};

export function CashEntryFormModal({ open, preset, onClose, onSaved }: Props) {
  const [code, setCode] = useState('');
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [offsetAccount, setOffsetAccount] = useState('');
  const [actor, setActor] = useState('');
  const [actorKind, setActorKind] = useState<'employee' | 'external'>('external');
  const [employees, setEmployees] = useState<EmployeePickerItem[]>([]);
  const [amountInput, setAmountInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !preset) return;
    const now = new Date();
    const dstr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const rnd = String(Math.floor(Math.random() * 99)).padStart(2, '0');
    const prefix = preset.method === 'cash_1111'
      ? (preset.entryType === 'receipt' ? 'PT' : 'PC')
      : 'UNC';
    setCode(`${prefix}-${dstr}-${rnd}`);
    setEntryDate(new Date().toISOString().slice(0, 10));
    setDescription('');
    setOffsetAccount(OFFSET_SUGGEST[preset.entryType][0].code);
    setActor('');
    setActorKind('external');
    setAmountInput('');
    setErr(null); setSaving(false);
  }, [open, preset]);

  useEffect(() => {
    if (!open) return;
    employeesApi.picker().then(setEmployees).catch(() => setEmployees([]));
  }, [open]);

  if (!open || !preset) return null;

  const amount = Number(amountInput.replace(/\D/g, '')) || 0;

  const title = preset.entryType === 'receipt'
    ? (preset.method === 'cash_1111' ? 'Lập phiếu thu tiền mặt (1111)' : 'Lập phiếu thu qua UNC (1121)')
    : (preset.method === 'cash_1111' ? 'Lập phiếu chi tiền mặt (1111)' : 'Lập phiếu chi qua UNC (1121)');

  const debitCol = preset.entryType === 'receipt' ? (preset.method === 'cash_1111' ? '1111' : '1121') : offsetAccount;
  const creditCol = preset.entryType === 'receipt' ? offsetAccount : (preset.method === 'cash_1111' ? '1111' : '1121');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (code.trim().length < 4) return setErr('Số CT tối thiểu 4 ký tự.');
    if (description.trim().length < 3) return setErr('Diễn giải tối thiểu 3 ký tự.');
    if (!offsetAccount.trim()) return setErr('Chọn TK đối ứng.');
    if (actor.trim().length < 2) return setErr('Nhập tên người nộp/nhận.');
    if (amount < 1000) return setErr('Số tiền tối thiểu 1.000đ.');
    setSaving(true);
    try {
      await cashEntriesApi.create({
        code: code.trim(),
        entryType: preset.entryType,
        method: preset.method,
        entryDate,
        description: description.trim(),
        offsetAccount: offsetAccount.trim(),
        actor: actor.trim(),
        amount,
      });
      onSaved();
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : String(e2)); }
    finally { setSaving(false); }
  };

  return (
    <div className="cvm-backdrop" onClick={onClose}>
      <div className="cvm-modal afm-modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="cvm-head">
            <div>
              <div className="cvm-crumb">KẾ TOÁN › SỔ QUỸ</div>
              <h2>{title}</h2>
              <p>Auto tạo bút toán đối ứng Nợ <b>{debitCol}</b> / Có <b>{creditCol}</b>.</p>
            </div>
            <button type="button" className="cvm-close" onClick={onClose}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg></button>
          </div>
          <div className="cvm-body">
            <div className="afm-grid-2">
              <div className="pfm-field">
                <label>Số CT <span className="req">*</span></label>
                <input value={code} onChange={(e) => setCode(e.target.value)} required />
              </div>
              <div className="pfm-field">
                <label>Ngày CT <span className="req">*</span></label>
                <input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} required />
              </div>
            </div>
            <div className="pfm-field">
              <label>Diễn giải <span className="req">*</span></label>
              <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="VD Thu tiền bán hàng lẻ / Chi mua VPP…" required />
            </div>
            <div className="afm-grid-2">
              <div className="pfm-field">
                <label>TK đối ứng <span className="req">*</span></label>
                <select value={offsetAccount} onChange={(e) => setOffsetAccount(e.target.value)}>
                  {OFFSET_SUGGEST[preset.entryType].map((o) => (
                    <option key={o.code} value={o.code}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="pfm-field">
                <label>{preset.entryType === 'receipt' ? 'Người nộp' : 'Người nhận'} <span className="req">*</span></label>
                <div className="cash-actor-kind">
                  <button type="button" className={actorKind === 'employee' ? 'active' : ''} onClick={() => setActorKind('employee')}>Nhân viên nội bộ</button>
                  <button type="button" className={actorKind === 'external' ? 'active' : ''} onClick={() => setActorKind('external')}>KH / NCC khác</button>
                </div>
                {actorKind === 'employee' ? (
                  <SearchableDropdown
                    value={actor}
                    onChange={setActor}
                    placeholder="Chọn nhân viên từ HR"
                    ariaLabel="Chọn nhân viên"
                    options={employees.map((employee) => ({ value: employee.name, label: `${employee.code ? `${employee.code} · ` : ''}${employee.name}${employee.position ? ` · ${employee.position}` : ''}` }))}
                  />
                ) : <input value={actor} onChange={(e) => setActor(e.target.value)} placeholder="Tên khách hàng / nhà cung cấp" required />}
              </div>
            </div>
            <div className="pfm-field">
              <label>Số tiền <span className="req">*</span></label>
              <input
                inputMode="numeric" placeholder="0"
                value={amount ? amount.toLocaleString('vi-VN') : ''}
                onChange={(e) => setAmountInput(e.target.value)} required
              />
            </div>
            {err && <div className="cvm-error">{err}</div>}
          </div>
          <div className="cvm-foot">
            <button type="button" className="pay-btn-outline" onClick={onClose} disabled={saving}>Huỷ</button>
            <button type="submit" className="pay-btn-primary" disabled={saving}>{saving ? 'Đang lưu…' : 'Lập phiếu'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
